/**
 * Cart Drawer Modal Guard
 * ADDED: 2026-08-18 by MeshPlanet
 *
 * Keeps the open cart drawer in the top layer across section morphs.
 *
 * The drawer is a modal <dialog> living inside the header section, so every
 * cart update morphs the markup around it. When the incoming HTML has a
 * different number of children than the live DOM somewhere above the dialog,
 * morph.js realigns by calling insertBefore on the node it already has -- which
 * moves it. Moving a node detaches it, and the browser drops a detached modal
 * dialog out of the top layer while leaving its `open` attribute alone. The
 * result is a drawer that no longer matches :modal: no ::backdrop, none of the
 * `.cart-drawer__dialog:modal` sizing, the panel rendered inline as a stray box
 * over the page, and <body> stuck at position:fixed because closeDialog() --
 * the only thing that unlocks it -- never ran.
 *
 * snippets/stylux-head.liquid was the known source of that child-count drift
 * and no longer feeds foreign-template markup to the morph, but any app can
 * re-render a cart section against the wrong URL, and the page is unusable when
 * one does. So the drawer re-enters the top layer on its own.
 *
 * Only a drawer this file saw open modally is restored, so a deliberate
 * non-modal dialog.show() elsewhere is left alone.
 */
(function () {
  'use strict';

  if (window.mpCartDrawerModalGuard) return;
  window.mpCartDrawerModalGuard = true;

  var DIALOG_SELECTOR = 'cart-drawer-component dialog.cart-drawer__dialog';

  /* Tracked as a flag rather than against the node itself: a morph can swap the
     dialog for a fresh element that inherited `open`, and that replacement needs
     restoring just as much as a moved one. */
  var expectModal = false;

  /** @type {MutationObserver | null} */
  var observer = null;

  /** @returns {HTMLDialogElement | null} */
  function currentDialog() {
    return /** @type {HTMLDialogElement | null} */ (document.querySelector(DIALOG_SELECTOR));
  }

  /**
   * Puts the dialog back in the top layer.
   *
   * showModal() throws on a dialog that still carries `open`, so it has to be
   * closed first. The opening animation is suppressed because this is a repair,
   * not a second open -- the shopper should not see the drawer slide in again.
   * move-and-fade ends on the element's natural transform and opacity, so
   * `animation: none` leaves the drawer exactly where the finished animation
   * would have. The inline value is left in place on purpose: closeDialog()
   * clears it before adding `.dialog-closing`, so the real close still animates.
   *
   * @param {HTMLDialogElement} dialog
   */
  function restore(dialog) {
    var content = dialog.querySelector('.cart-drawer__content');
    var scrollTop = content ? content.scrollTop : 0;

    dialog.style.animation = 'none';
    /* Force a reflow so the suppressed animation is committed before `open` is
       taken away and given back. */
    void dialog.offsetWidth;

    try {
      dialog.close();
      dialog.showModal();
    } catch (error) {
      console.error('Cart drawer modal guard could not restore the dialog:', error);
      dialog.style.animation = '';
      stop();
      return;
    }

    if (content) content.scrollTop = scrollTop;
  }

  function check() {
    if (!expectModal) return stop();

    var dialog = currentDialog();

    if (!dialog || !dialog.hasAttribute('open')) return stop();
    if (!dialog.isConnected || dialog.matches(':modal')) return;

    restore(dialog);
  }

  /* childList only: restore() only changes attributes, so it cannot retrigger
     this callback. */
  function start() {
    if (observer) return;

    observer = new MutationObserver(check);
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function stop() {
    expectModal = false;

    if (!observer) return;

    observer.disconnect();
    observer = null;
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  function isCartDrawer(event) {
    return event.target instanceof Element && event.target.matches('cart-drawer-component');
  }

  /* DialogOpenEvent and DialogCloseEvent do not bubble, but a capturing listener
     on the document still runs on the way down to the target. */
  document.addEventListener(
    'dialog:open',
    function (event) {
      if (!isCartDrawer(event)) return;

      var dialog = currentDialog();
      if (!dialog || !dialog.matches(':modal')) return;

      expectModal = true;
      start();
    },
    true
  );

  document.addEventListener(
    'dialog:close',
    function (event) {
      if (!isCartDrawer(event)) return;

      stop();
    },
    true
  );
})();
